/**
 * Deep Link Navigation Hook
 *
 * Handles navigation when page loads with a comment hash.
 * Detects hash, waits for comments to load, then scrolls/opens the comment.
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import type { Comment, MatchResult } from '../lib/types';
import type { ToastType } from './useToast';
import { getCommentIdFromHash, clearCommentHash } from '../utils/deepLinkUtils';

interface UseDeepLinkNavigationOptions {
  comments: Comment[];
  matchResults: Map<string, MatchResult>;
  isLoading: boolean;
  onExpandComment: (commentId: string, element: HTMLElement | null) => void;
  onOpenSidebarWithComment: (commentId: string) => void;
  showToast: (message: string, type: ToastType) => void;
}

interface NavigationState {
  pendingCommentId: string | null;
  loadingToastId: string | null;
}

const SCROLL_TO_OPEN_DELAY_MS = 300;

/**
 * Hook to handle deep link navigation to comments.
 * Detects comment hash on mount and navigates when comments are loaded.
 */
export function useDeepLinkNavigation({
  comments,
  matchResults,
  isLoading,
  onExpandComment,
  onOpenSidebarWithComment,
  showToast,
}: UseDeepLinkNavigationOptions): void {
  // Track navigation state
  const [state, setState] = useState<NavigationState>({
    pendingCommentId: null,
    loadingToastId: null,
  });

  // Ref to prevent duplicate navigation
  const hasNavigated = useRef(false);

  // Detect hash on mount and on hash changes
  const detectAndSetPendingComment = useCallback(() => {
    const commentId = getCommentIdFromHash();
    if (commentId) {
      // Reset navigation state for new comment
      hasNavigated.current = false;
      setState({
        pendingCommentId: commentId,
        loadingToastId: null,
      });
      // Show loading toast
      showToast('Opening comment...', 'info');
    }
  }, [showToast]);

  // Detect hash on mount
  useEffect(() => {
    detectAndSetPendingComment();
  }, [detectAndSetPendingComment]);

  // Listen for hash change events
  useEffect(() => {
    const handleHashChange = () => {
      detectAndSetPendingComment();
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [detectAndSetPendingComment]);

  // Navigate when comments are loaded and we have a pending comment
  useEffect(() => {
    // Skip if already navigated, still loading, or no pending comment
    if (hasNavigated.current || isLoading || !state.pendingCommentId) {
      return;
    }

    const commentId = state.pendingCommentId;

    // Find the comment in the comments array
    const comment = comments.find((c) => c.id === commentId);

    if (!comment) {
      // Comment not found - handle error
      handleCommentNotFound(commentId);
      return;
    }

    // Comment exists - check if we have a matching element
    const matchResult = matchResults.get(commentId);

    if (matchResult?.element) {
      // Success: scroll to element and open thread
      navigateToComment(commentId, matchResult.element as HTMLElement);
    } else {
      // Element not found - fall back to sidebar
      handleElementNotFound(commentId);
    }
  }, [comments, matchResults, isLoading, state.pendingCommentId]);

  /**
   * Navigate to a comment: scroll to element and open thread
   */
  const navigateToComment = useCallback(
    (commentId: string, element: HTMLElement) => {
      hasNavigated.current = true;

      // Scroll element into view
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
        inline: 'center',
      });

      // Open thread after scroll completes
      setTimeout(() => {
        onExpandComment(commentId, element);
      }, SCROLL_TO_OPEN_DELAY_MS);

      // Clear pending state
      setState({
        pendingCommentId: null,
        loadingToastId: null,
      });
    },
    [onExpandComment]
  );

  /**
   * Handle case where comment doesn't exist
   */
  const handleCommentNotFound = useCallback(
    (_commentId: string) => {
      hasNavigated.current = true;

      // Check if page has any comments (might be wrong page vs deleted comment)
      const pageHasComments = comments.length > 0;

      if (pageHasComments) {
        // Likely wrong page - keep hash so user can navigate
        showToast('Comment not found on this page', 'error');
      } else {
        // Likely deleted comment - clear hash
        showToast('Comment not found', 'error');
        clearCommentHash();
      }

      setState({
        pendingCommentId: null,
        loadingToastId: null,
      });
    },
    [comments.length, showToast]
  );

  /**
   * Handle case where comment exists but element is not matched
   */
  const handleElementNotFound = useCallback(
    (commentId: string) => {
      hasNavigated.current = true;

      showToast('Element not found - showing in sidebar', 'info');
      onOpenSidebarWithComment(commentId);

      // Keep hash in URL (comment is still accessible via sidebar)
      setState({
        pendingCommentId: null,
        loadingToastId: null,
      });
    },
    [showToast, onOpenSidebarWithComment]
  );
}
